import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ScrollText, Plus, Search, Download, FileUp, FileText, CheckCircle2,
  Clock, AlertCircle, Trash2, Pencil, Loader2, X, Users, Upload,
  Ban, ExternalLink, History, ShieldCheck, DollarSign, BookMarked, Flag, AlertTriangle, XCircle,
} from "lucide-react";

const ESTADO_META = {
  sin_subir:        { label: "Sin Subir",        icon: AlertCircle,   dot: "#B88537", bg: "#FBEED4", fg: "#7A5312", border: "#E5C892" },
  en_tramite:       { label: "En Trámite",       icon: Clock,         dot: "#D97706", bg: "#FEE9CC", fg: "#8A4A00", border: "#F3B872" },
  corregir_defecto: { label: "Corregir Defecto", icon: AlertTriangle, dot: "#7C3AED", bg: "#EDE7F6", fg: "#4527A0", border: "#B39DDB" },
  completada:       { label: "Completada",       icon: CheckCircle2,  dot: "#16A34A", bg: "#D8EFDD", fg: "#155724", border: "#7BC48A" },
  cancelada:        { label: "Cancelada",        icon: XCircle,       dot: "#6B7280", bg: "#F3F4F6", fg: "#374151", border: "#9CA3AF" },
  anulada:          { label: "Anulada",          icon: Ban,           dot: "#DC2626", bg: "#FADCDC", fg: "#7A1F1F", border: "#EAA0A0" },
};

// Estados que requieren nota/motivo antes de cambiar
const ESTADOS_CON_MOTIVO = {
  corregir_defecto: {
    title: "Corregir defecto",
    icon: AlertTriangle,
    accent: "#7C3AED",
    accentHover: "#6D28D9",
    label: "Describe brevemente el defecto que debe corregirse. La nota queda registrada en el historial.",
    placeholder: "Ej: Falta razón de referencia del asiento anterior; error en cédula del compareciente…",
    confirm: "Marcar Corregir defecto",
  },
  cancelada: {
    title: "Cancelar escritura",
    icon: XCircle,
    accent: "#6B7280",
    accentHover: "#4B5563",
    label: "Indica el motivo por el cual se cancela la escritura. Este motivo queda registrado en el historial.",
    placeholder: "Ej: El cliente desistió del acto; falta de fondos; documentación incompleta no subsanada…",
    confirm: "Cancelar escritura",
  },
};

const EMPTY = {
  numero: "",
  tipo: "",
  tipo_custom: "",
  tomo: "",
  monto: "",
  moneda: "CRC",
  folio_inicio: { numero: "", lado: "frente" },
  folio_fin: { numero: "", lado: "frente" },
  fecha: "",
  hora: "",
  comparecientes: [""],
  asiento: "",
  cliente_id: "",
  notas: "",
};

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";

function EstadoBadge({ estado }) {
  const m = ESTADO_META[estado] || ESTADO_META.sin_subir;
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border"
      style={{ backgroundColor: m.bg, color: m.fg, borderColor: m.border }}
    >
      <Icon className="h-3 w-3" strokeWidth={2.2} /> {m.label}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, tint = "#7A1E2E", testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl bg-white border border-[#E5DFD3] p-5 flex items-center gap-4 hover:border-[#C25A5A] transition-colors"
    >
      <div
        className="h-11 w-11 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${tint}12`, border: `1px solid ${tint}30` }}
      >
        <Icon className="h-5 w-5" strokeWidth={1.8} style={{ color: tint }} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">{title}</div>
        <div className="mt-0.5 font-display text-2xl text-[#1A0810]">{value}</div>
      </div>
    </div>
  );
}

function folioLabel(f) {
  if (!f) return "";
  const n = f.numero ?? "";
  const l = (f.lado || "").charAt(0).toUpperCase() + (f.lado || "").slice(1);
  return n === "" || n === null ? "" : `${n} ${l}`.trim();
}

function formatFecha(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y) return iso;
  return `${d}/${m}/${y}`;
}

function formatMoney(v, moneda = "CRC") {
  if (!v || Number(v) === 0) return "—";
  const symbol = moneda === "USD" ? "$" : "₡";
  try {
    return `${symbol} ${Number(v).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch (_) {
    return `${symbol} ${v}`;
  }
}

function tipoDisplay(e) {
  if (!e) return "";
  if (e.tipo === "Otro" && e.tipo_custom) return `Otro — ${e.tipo_custom}`;
  return e.tipo;
}

export default function Notarial() {
  const { user } = useAuth();
  const isAbogadoPrincipal = !!user?.is_abogado_principal;

  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ total: 0, sin_subir: 0, en_tramite: 0, completada: 0, anulada: 0 });
  const [config, setConfig] = useState({ tipos: [], estados: [] });
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("todos");
  const [anioFilter, setAnioFilter] = useState("todos");
  const [tipoFilter, setTipoFilter] = useState("todos");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [pdfDialog, setPdfDialog] = useState(null);
  const [historyDialog, setHistoryDialog] = useState(null);
  const [motivoDialog, setMotivoDialog] = useState(null); // { escritura, estado, nota }

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportRange, setExportRange] = useState({ desde: "", hasta: "", estado: "todos" });

  const load = async () => {
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (estadoFilter !== "todos") params.estado = estadoFilter;
      if (anioFilter !== "todos") params.anio = anioFilter;
      if (tipoFilter !== "todos") params.tipo = tipoFilter;
      const [lst, st] = await Promise.all([
        api.get("/escrituras", { params }),
        api.get("/escrituras/stats"),
      ]);
      setList(lst.data || []);
      setStats(st.data || {});
    } catch (e) {
      toast.error("No se pudo cargar el registro notarial");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cfg, cli] = await Promise.all([
          api.get("/escrituras/config"),
          api.get("/clientes"),
        ]);
        setConfig(cfg.data);
        setClientes(cli.data || []);
      } catch (_) {}
      await load();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, estadoFilter, anioFilter, tipoFilter]);

  const availableYears = useMemo(() => {
    const yrs = new Set();
    list.forEach((e) => { if (e.fecha) yrs.add(e.fecha.slice(0, 4)); });
    yrs.add(String(new Date().getFullYear()));
    return Array.from(yrs).filter(Boolean).sort((a, b) => b.localeCompare(a));
  }, [list]);

  const openNew = () => {
    setEditing(null);
    setForm({
      ...EMPTY,
      fecha: new Date().toISOString().slice(0, 10),
      tipo: config.tipos?.[0] || "",
      moneda: "CRC",
    });
    setDialogOpen(true);
  };

  const openEdit = (esc) => {
    setEditing(esc);
    setForm({
      numero: esc.numero,
      tipo: esc.tipo,
      tipo_custom: esc.tipo_custom || "",
      tomo: esc.tomo || "",
      monto: esc.monto || "",
      moneda: esc.moneda || "CRC",
      folio_inicio: { numero: esc.folio_inicio?.numero ?? "", lado: esc.folio_inicio?.lado || "frente" },
      folio_fin: { numero: esc.folio_fin?.numero ?? "", lado: esc.folio_fin?.lado || "frente" },
      fecha: esc.fecha || "",
      hora: esc.hora || "",
      comparecientes: (esc.comparecientes && esc.comparecientes.length) ? [...esc.comparecientes] : [""],
      asiento: esc.asiento || "",
      cliente_id: esc.cliente_id || "",
      notas: esc.notas || "",
    });
    setDialogOpen(true);
  };

  const saveForm = async () => {
    if (!form.numero || Number(form.numero) <= 0) { toast.error("Número de escritura inválido"); return; }
    if (!form.tipo) { toast.error("Selecciona el tipo de escritura"); return; }
    if (form.tipo === "Otro" && !form.tipo_custom.trim()) { toast.error("Escribe el nombre del tipo"); return; }
    setSaving(true);
    try {
      const payload = {
        numero: Number(form.numero),
        tipo: form.tipo,
        tipo_custom: form.tipo === "Otro" ? form.tipo_custom.trim() : "",
        tomo: Number(form.tomo || 0),
        monto: Number(form.monto || 0),
        moneda: form.moneda || "CRC",
        folio_inicio: { numero: Number(form.folio_inicio.numero || 0), lado: form.folio_inicio.lado },
        folio_fin: { numero: Number(form.folio_fin.numero || 0), lado: form.folio_fin.lado },
        fecha: form.fecha,
        hora: form.hora,
        comparecientes: form.comparecientes.map((c) => c.trim()).filter(Boolean),
        asiento: form.asiento,
        cliente_id: form.cliente_id || null,
        notas: form.notas,
      };
      if (editing) {
        await api.put(`/escrituras/${editing.id}`, payload);
        toast.success("Escritura actualizada");
      } else {
        await api.post(`/escrituras`, payload);
        toast.success("Escritura registrada");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const removeEscritura = async (esc) => {
    if (!window.confirm(`¿Eliminar la escritura N° ${esc.numero}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/escrituras/${esc.id}`);
      toast.success("Escritura eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const changeEstado = async (esc, nuevo, nota = "") => {
    if (nuevo === esc.estado) return;
    // Requiere nota si cambia a un estado con motivo obligatorio
    if (ESTADOS_CON_MOTIVO[nuevo] && !nota) {
      setMotivoDialog({ escritura: esc, estado: nuevo, nota: "" });
      return;
    }
    try {
      await api.post(`/escrituras/${esc.id}/estado`, { estado: nuevo, nota });
      toast.success(`Estado actualizado a ${ESTADO_META[nuevo]?.label || nuevo}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cambiar el estado");
    }
  };

  const confirmarMotivo = async () => {
    if (!motivoDialog) return;
    const nota = (motivoDialog.nota || "").trim();
    if (!nota) { toast.error("Escribe el motivo antes de continuar"); return; }
    await changeEstado(motivoDialog.escritura, motivoDialog.estado, nota);
    setMotivoDialog(null);
  };

  const finalizar = async (esc) => {
    if (!esc.pdf_filename) {
      toast.error("Debe adjuntar el PDF de la escritura antes de finalizarla");
      return;
    }
    if (!window.confirm(`¿Marcar como Completada la escritura N° ${esc.numero}? Esta acción quedará registrada en el historial.`)) return;
    await changeEstado(esc, "completada");
  };

  const uploadPdf = async (esc, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Solo se aceptan archivos PDF"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("El PDF supera 15 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/escrituras/${esc.id}/pdf`, { filename: file.name, pdf_base64: reader.result });
        toast.success("PDF adjuntado");
        await load();
        setPdfDialog(null);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo subir el PDF");
      }
    };
    reader.readAsDataURL(file);
  };

  const removePdf = async (esc) => {
    if (!window.confirm("¿Eliminar el PDF adjunto?")) return;
    try {
      await api.delete(`/escrituras/${esc.id}/pdf`);
      toast.success("PDF eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar el PDF");
    }
  };

  const viewPdf = async (esc) => {
    try {
      const res = await api.get(`/escrituras/${esc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) {
      toast.error("No se pudo abrir el PDF");
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (exportRange.desde) params.fecha_desde = exportRange.desde;
      if (exportRange.hasta) params.fecha_hasta = exportRange.hasta;
      if (exportRange.estado !== "todos") params.estado = exportRange.estado;
      const res = await api.get(`/escrituras/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const suffix = exportRange.desde || exportRange.hasta ? `_${exportRange.desde || "inicio"}_a_${exportRange.hasta || "hoy"}` : "";
      a.href = url;
      a.download = `registro_escrituras${suffix}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
      toast.success("CSV descargado");
    } catch (_) {
      toast.error("No se pudo exportar el registro");
    }
  };

  const clienteMap = useMemo(() => Object.fromEntries((clientes || []).map((c) => [c.id, c.nombre])), [clientes]);

  const canEditRow = (esc) => esc.estado !== "completada" || isAbogadoPrincipal;
  const canChangeState = (esc, nuevo) => {
    if (nuevo === "completada") return isAbogadoPrincipal;
    if (esc.estado === "completada") return isAbogadoPrincipal;
    return true;
  };

  return (
    <div className="space-y-8" data-testid="module-notarial">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Módulo</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl text-[#1A0810]">Notarial</h1>
          <p className="mt-2 text-sm text-[#4a3838] max-w-2xl">
            Registro cronológico de escrituras, protocolos y actos notariales del bufete.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="btn-export-csv"
            onClick={() => setExportDialogOpen(true)}
            className="h-10 border-[#E5DFD3] text-[#3d2a2a] hover:bg-[#F5EEE0] hover:text-[#7A1E2E]"
          >
            <Download className="h-4 w-4 mr-2" strokeWidth={1.8} /> Exportar CSV
          </Button>
          <Button
            data-testid="btn-nueva-escritura"
            onClick={openNew}
            className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]"
          >
            <Plus className="h-4 w-4 mr-2" strokeWidth={2} /> Nueva escritura
          </Button>
        </div>
      </div>

      {isAbogadoPrincipal && (
        <div className="rounded-xl bg-gradient-to-r from-[#7A1E2E] to-[#5F1723] text-[#F1EDE4] p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#F1EDE4]/10 border border-[#F1EDE4]/20 flex items-center justify-center">
            <ShieldCheck className="h-4 w-4" strokeWidth={2} />
          </div>
          <div className="text-sm">
            Iniciaste sesión como <span className="font-semibold">abogado principal</span>. Solo tú puedes <span className="font-semibold">finalizar</span> escrituras y eliminarlas del registro.
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <StatCard testId="stat-total" title="Total" value={stats.total} icon={ScrollText} tint="#7A1E2E" />
        <StatCard testId="stat-sin-subir" title="Sin subir" value={stats.sin_subir} icon={AlertCircle} tint="#B88537" />
        <StatCard testId="stat-en-tramite" title="En trámite" value={stats.en_tramite} icon={Clock} tint="#D97706" />
        <StatCard testId="stat-corregir-defecto" title="Corregir defecto" value={stats.corregir_defecto} icon={AlertTriangle} tint="#7C3AED" />
        <StatCard testId="stat-completadas" title="Completadas" value={stats.completada} icon={CheckCircle2} tint="#16A34A" />
        <StatCard testId="stat-canceladas" title="Canceladas" value={stats.cancelada} icon={XCircle} tint="#6B7280" />
        <StatCard testId="stat-anuladas" title="Anuladas" value={stats.anulada} icon={Ban} tint="#DC2626" />
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
          <Input
            data-testid="input-search"
            placeholder="Buscar por número, tipo, compareciente, asiento…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 pl-10 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]"
          />
        </div>
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger data-testid="filter-estado" className="h-11 w-full md:w-[170px] bg-white border-[#E5DFD3]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {config.estados?.map((s) => (<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger data-testid="filter-tipo" className="h-11 w-full md:w-[220px] bg-white border-[#E5DFD3]"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[280px]">
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {config.tipos?.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={anioFilter} onValueChange={setAnioFilter}>
          <SelectTrigger data-testid="filter-anio" className="h-11 w-full md:w-[130px] bg-white border-[#E5DFD3]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los años</SelectItem>
            {availableYears.map((y) => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5EEE0] border-b border-[#E5DFD3]">
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#6B5D57] font-semibold">
                <th className="px-3 py-3 w-14">N°</th>
                <th className="px-3 py-3 w-14">Tomo</th>
                <th className="px-3 py-3">Tipo</th>
                <th className="px-3 py-3 w-28">Folio</th>
                <th className="px-3 py-3 w-28">Fecha / Hora</th>
                <th className="px-3 py-3">Comparecientes</th>
                <th className="px-3 py-3 w-24">Asiento</th>
                <th className="px-3 py-3 w-28">Monto</th>
                <th className="px-3 py-3 w-40">Estado</th>
                <th className="px-3 py-3 w-14">PDF</th>
                <th className="px-3 py-3 w-40 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-[#6B5D57]"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></td></tr>
              )}
              {!loading && list.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-16 text-center">
                    <ScrollText className="h-8 w-8 mx-auto text-[#C25A5A]" strokeWidth={1.5} />
                    <div className="mt-3 font-display text-lg text-[#1A0810]">Aún no hay escrituras registradas</div>
                    <div className="mt-1 text-xs text-[#6B5D57]">Presiona "Nueva escritura" para comenzar el registro.</div>
                  </td>
                </tr>
              )}
              {!loading && list.map((e) => {
                const meta = ESTADO_META[e.estado] || ESTADO_META.sin_subir;
                return (
                  <tr
                    key={e.id}
                    data-testid={`escritura-row-${e.numero}`}
                    className="border-b border-[#F0EAE0] last:border-b-0 hover:bg-[#FAF7F0] transition-colors relative"
                    style={{ borderLeft: `3px solid ${meta.dot}` }}
                  >
                    <td className="px-3 py-3 font-display text-[#1A0810] font-semibold">{e.numero}</td>
                    <td className="px-3 py-3 text-[#3d2a2a] text-xs">{e.tomo || "—"}</td>
                    <td className="px-3 py-3 text-[#3d2a2a]">{tipoDisplay(e)}</td>
                    <td className="px-3 py-3 text-[#3d2a2a]">
                      <div className="text-xs">{folioLabel(e.folio_inicio)}</div>
                      <div className="text-xs text-[#6B5D57]">→ {folioLabel(e.folio_fin)}</div>
                    </td>
                    <td className="px-3 py-3 text-[#3d2a2a]">
                      <div className="text-xs">{formatFecha(e.fecha)}</div>
                      <div className="text-xs text-[#6B5D57]">{e.hora || ""}</div>
                    </td>
                    <td className="px-3 py-3 text-[#3d2a2a] max-w-xs">
                      <div className="flex flex-wrap gap-1">
                        {(e.comparecientes || []).slice(0, 3).map((c, i) => (
                          <span key={i} className="inline-flex text-[11px] bg-[#F5EEE0] border border-[#E5DFD3] px-2 py-0.5 rounded-full text-[#3d2a2a]">
                            {c}
                          </span>
                        ))}
                        {(e.comparecientes || []).length > 3 && (
                          <span className="text-[11px] text-[#6B5D57]">+{e.comparecientes.length - 3} más</span>
                        )}
                      </div>
                      {e.cliente_id && clienteMap[e.cliente_id] && (
                        <div className="mt-1 text-[10px] text-[#7A1E2E] font-semibold uppercase tracking-wider">
                          Cliente: {clienteMap[e.cliente_id]}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-[#3d2a2a] text-xs font-mono">{e.asiento || "—"}</td>
                    <td className="px-3 py-3 text-[#3d2a2a] text-xs font-semibold">{formatMoney(e.monto, e.moneda)}</td>
                    <td className="px-3 py-3">
                      {/* Dropdown de estado como en el Excel */}
                      <Select
                        value={e.estado}
                        onValueChange={(v) => changeEstado(e, v)}
                        disabled={e.estado === "completada" && !isAbogadoPrincipal}
                      >
                        <SelectTrigger
                          data-testid={`estado-select-${e.numero}`}
                          className="h-8 text-xs font-semibold border-0 shadow-none px-2 gap-1"
                          style={{ backgroundColor: meta.bg, color: meta.fg, border: `1px solid ${meta.border}` }}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {config.estados?.map((s) => {
                            const disabledOpt = !canChangeState(e, s.key);
                            return (
                              <SelectItem key={s.key} value={s.key} disabled={disabledOpt}>
                                <span className="inline-flex items-center gap-2">
                                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ESTADO_META[s.key]?.dot }} />
                                  {s.label}
                                  {disabledOpt && s.key === "completada" && (
                                    <span className="text-[10px] text-[#6B5D57]">(solo abogado principal)</span>
                                  )}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-3">
                      {e.pdf_filename ? (
                        <button
                          data-testid={`btn-view-pdf-${e.numero}`}
                          onClick={() => viewPdf(e)}
                          className="inline-flex items-center gap-1 text-[#7A1E2E] hover:text-[#5F1723] text-xs font-semibold"
                          title={e.pdf_filename}
                        >
                          <FileText className="h-4 w-4" strokeWidth={1.8} /> Ver
                        </button>
                      ) : (
                        <button
                          data-testid={`btn-upload-pdf-${e.numero}`}
                          onClick={() => setPdfDialog(e)}
                          className="inline-flex items-center gap-1 text-[#6B5D57] hover:text-[#7A1E2E] text-xs"
                          title="Adjuntar PDF"
                        >
                          <FileUp className="h-4 w-4" strokeWidth={1.8} /> Subir
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Botón FINALIZAR — solo abogado principal, solo si no está completada */}
                        {isAbogadoPrincipal && e.estado !== "completada" && e.estado !== "anulada" && (
                          <button
                            data-testid={`btn-finalizar-${e.numero}`}
                            onClick={() => finalizar(e)}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#16A34A] hover:bg-[#128036] text-white text-[11px] font-semibold uppercase tracking-wider"
                            title="Finalizar escritura"
                          >
                            <Flag className="h-3.5 w-3.5" strokeWidth={2.2} /> Finalizar
                          </button>
                        )}
                        {e.estado === "completada" && (
                          <span
                            data-testid={`badge-completada-${e.numero}`}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#D8EFDD] text-[#155724] border border-[#7BC48A] text-[11px] font-semibold uppercase tracking-wider"
                            title="Escritura finalizada"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.2} /> Finalizada
                          </span>
                        )}
                        <button
                          data-testid={`btn-history-${e.numero}`}
                          onClick={() => setHistoryDialog(e)}
                          className="h-8 w-8 rounded-md hover:bg-[#F5EEE0] flex items-center justify-center text-[#6B5D57] hover:text-[#7A1E2E]"
                          title="Historial"
                        >
                          <History className="h-4 w-4" strokeWidth={1.8} />
                        </button>
                        <button
                          data-testid={`btn-edit-${e.numero}`}
                          onClick={() => openEdit(e)}
                          className="h-8 w-8 rounded-md hover:bg-[#F5EEE0] flex items-center justify-center text-[#6B5D57] hover:text-[#7A1E2E] disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Editar"
                          disabled={!canEditRow(e)}
                        >
                          <Pencil className="h-4 w-4" strokeWidth={1.8} />
                        </button>
                        {isAbogadoPrincipal && (
                          <button
                            data-testid={`btn-delete-${e.numero}`}
                            onClick={() => removeEscritura(e)}
                            className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#6B5D57] hover:text-red-700"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl bg-[#FAF7F0] border-[#E5DFD3] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">
              {editing ? `Editar escritura N° ${editing.numero}` : "Nueva escritura"}
            </DialogTitle>
            <DialogDescription className="text-[#6B5D57]">
              Registra los datos de la escritura conforme al tomo del protocolo notarial.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Número</Label>
              <Input data-testid="form-numero" type="number" min={1} value={form.numero}
                onChange={(e) => setForm({ ...form, numero: e.target.value })}
                className={`mt-2 ${inputCls}`} placeholder="Ej: 128" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold flex items-center gap-1.5">
                <BookMarked className="h-3 w-3" strokeWidth={1.8} /> Tomo
              </Label>
              <Input data-testid="form-tomo" type="number" min={0} value={form.tomo}
                onChange={(e) => setForm({ ...form, tomo: e.target.value })}
                className={`mt-2 ${inputCls}`} placeholder="Ej: 5" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Tipo de escritura</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="form-tipo" className={`mt-2 ${inputCls}`}><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {config.tipos?.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.tipo === "Otro" && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#7A1E2E] font-semibold">
                Nombre personalizado del tipo
              </Label>
              <Input
                data-testid="form-tipo-custom"
                value={form.tipo_custom}
                onChange={(e) => setForm({ ...form, tipo_custom: e.target.value })}
                className={`mt-2 ${inputCls}`}
                placeholder="Ej: Rectificación de linderos"
              />
              <p className="mt-1 text-[11px] text-[#6B5D57]">Este nombre aparecerá en el registro y en el CSV exportado.</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Folio inicio</Label>
              <div className="mt-2 flex gap-2">
                <Input data-testid="form-folio-inicio-num" type="number" min={0}
                  value={form.folio_inicio.numero}
                  onChange={(e) => setForm({ ...form, folio_inicio: { ...form.folio_inicio, numero: e.target.value } })}
                  className={`flex-1 ${inputCls}`} placeholder="N°" />
                <Select value={form.folio_inicio.lado} onValueChange={(v) => setForm({ ...form, folio_inicio: { ...form.folio_inicio, lado: v } })}>
                  <SelectTrigger className={`w-[130px] ${inputCls}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="frente">Frente</SelectItem>
                    <SelectItem value="vuelto">Vuelto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Folio fin</Label>
              <div className="mt-2 flex gap-2">
                <Input data-testid="form-folio-fin-num" type="number" min={0}
                  value={form.folio_fin.numero}
                  onChange={(e) => setForm({ ...form, folio_fin: { ...form.folio_fin, numero: e.target.value } })}
                  className={`flex-1 ${inputCls}`} placeholder="N°" />
                <Select value={form.folio_fin.lado} onValueChange={(v) => setForm({ ...form, folio_fin: { ...form.folio_fin, lado: v } })}>
                  <SelectTrigger className={`w-[130px] ${inputCls}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="frente">Frente</SelectItem>
                    <SelectItem value="vuelto">Vuelto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Fecha</Label>
              <Input data-testid="form-fecha" type="date" value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className={`mt-2 ${inputCls}`} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Hora</Label>
              <Input data-testid="form-hora" type="time" value={form.hora}
                onChange={(e) => setForm({ ...form, hora: e.target.value })}
                className={`mt-2 ${inputCls}`} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Asiento</Label>
              <Input data-testid="form-asiento" value={form.asiento}
                onChange={(e) => setForm({ ...form, asiento: e.target.value })}
                className={`mt-2 ${inputCls}`} placeholder="Ej: A123456" />
            </div>
          </div>

          {/* Monto cobrado */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold flex items-center gap-1.5">
                <DollarSign className="h-3 w-3" strokeWidth={1.8} /> Monto cobrado
              </Label>
              <Input
                data-testid="form-monto"
                type="number"
                min={0}
                step="0.01"
                value={form.monto}
                onChange={(e) => setForm({ ...form, monto: e.target.value })}
                className={`mt-2 ${inputCls}`}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Moneda</Label>
              <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                <SelectTrigger data-testid="form-moneda" className={`mt-2 ${inputCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRC">₡ Colones</SelectItem>
                  <SelectItem value="USD">$ Dólares</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold flex items-center gap-1.5">
                <Users className="h-3 w-3" strokeWidth={1.8} /> Comparecientes
              </Label>
              <button
                type="button"
                onClick={() => setForm({ ...form, comparecientes: [...form.comparecientes, ""] })}
                className="text-[11px] uppercase tracking-wider text-[#7A1E2E] font-semibold hover:underline flex items-center gap-1"
              >
                <Plus className="h-3 w-3" strokeWidth={2} /> Agregar
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {form.comparecientes.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    data-testid={`form-compareciente-${i}`}
                    value={c}
                    onChange={(e) => {
                      const arr = [...form.comparecientes];
                      arr[i] = e.target.value;
                      setForm({ ...form, comparecientes: arr });
                    }}
                    className={inputCls}
                    placeholder="Nombre completo"
                  />
                  {form.comparecientes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, comparecientes: form.comparecientes.filter((_, j) => j !== i) })}
                      className="h-11 w-11 rounded-md border border-[#E5DFD3] bg-white flex items-center justify-center text-[#6B5D57] hover:bg-red-50 hover:text-red-700"
                    >
                      <X className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Cliente vinculado (opcional)</Label>
            <Select value={form.cliente_id || "none"} onValueChange={(v) => setForm({ ...form, cliente_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="form-cliente" className={`mt-2 ${inputCls}`}><SelectValue placeholder="Sin cliente" /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                <SelectItem value="none">Sin cliente vinculado</SelectItem>
                {clientes.map((c) => (<SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Notas</Label>
            <Textarea
              data-testid="form-notas"
              value={form.notas}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
              className="mt-2 min-h-[88px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]"
              placeholder="Detalles internos, observaciones, referencias…"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-10 border-[#E5DFD3] text-[#3d2a2a]">Cancelar</Button>
            <Button
              data-testid="btn-save-escritura"
              onClick={saveForm}
              disabled={saving}
              className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editing ? "Guardar cambios" : "Registrar escritura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF upload dialog */}
      <Dialog open={!!pdfDialog} onOpenChange={(v) => !v && setPdfDialog(null)}>
        <DialogContent className="max-w-lg bg-[#FAF7F0] border-[#E5DFD3]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A0810]">
              Adjuntar PDF a escritura N° {pdfDialog?.numero}
            </DialogTitle>
            <DialogDescription className="text-[#6B5D57]">
              Sube el PDF firmado y sellado de la escritura. Máximo 15 MB.
            </DialogDescription>
          </DialogHeader>
          <label className="block rounded-xl border-2 border-dashed border-[#E5DFD3] bg-white p-8 text-center cursor-pointer hover:border-[#C25A5A] hover:bg-[#F5EEE0] transition-colors">
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              data-testid="input-pdf-file"
              onChange={(e) => uploadPdf(pdfDialog, e.target.files?.[0])}
            />
            <Upload className="h-8 w-8 mx-auto text-[#7A1E2E]" strokeWidth={1.5} />
            <div className="mt-3 font-display text-lg text-[#1A0810]">Selecciona el archivo PDF</div>
            <div className="mt-1 text-xs text-[#6B5D57]">Haz clic o arrastra el archivo aquí</div>
          </label>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyDialog} onOpenChange={(v) => !v && setHistoryDialog(null)}>
        <DialogContent className="max-w-2xl bg-[#FAF7F0] border-[#E5DFD3] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A0810]">
              Historial · Escritura N° {historyDialog?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {historyDialog?.pdf_filename && (
              <div className="rounded-lg bg-white border border-[#E5DFD3] p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.8} />
                  <div>
                    <div className="text-sm font-semibold text-[#1A0810]">{historyDialog.pdf_filename}</div>
                    <div className="text-[11px] text-[#6B5D57]">
                      {(historyDialog.pdf_size / 1024).toFixed(1)} KB · Subido por {historyDialog.pdf_uploaded_by}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => viewPdf(historyDialog)} className="h-8 w-8 rounded-md hover:bg-[#F5EEE0] flex items-center justify-center text-[#7A1E2E]" title="Ver PDF"><ExternalLink className="h-4 w-4" strokeWidth={1.8}/></button>
                  {(historyDialog.estado !== "completada" || isAbogadoPrincipal) && (
                    <button onClick={() => { removePdf(historyDialog); setHistoryDialog(null); }} className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#6B5D57] hover:text-red-700" title="Eliminar PDF"><Trash2 className="h-4 w-4" strokeWidth={1.8}/></button>
                  )}
                </div>
              </div>
            )}
            <div className="border-l-2 border-[#E5DFD3] pl-4 space-y-3">
              {(historyDialog?.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[21px] top-1 h-3 w-3 rounded-full bg-[#7A1E2E] border-2 border-[#FAF7F0]" />
                  <div className="text-sm font-semibold text-[#1A0810] capitalize">{h.action.replace(":", " · ").replace(/->/, " → ")}</div>
                  <div className="text-[11px] text-[#6B5D57]">
                    {new Date(h.at).toLocaleString("es-CR", { dateStyle: "medium", timeStyle: "short" })} · {h.by_name}
                  </div>
                  {h.nota && (
                    <div className="mt-1.5 text-[12px] text-[#4527A0] bg-[#EDE7F6] border border-[#B39DDB] rounded-md px-2.5 py-1.5 leading-relaxed">
                      <span className="font-semibold uppercase tracking-wider text-[10px]">Nota:</span> {h.nota}
                    </div>
                  )}
                </div>
              ))}
              {(historyDialog?.history || []).length === 0 && (
                <div className="text-sm text-[#6B5D57]">Sin eventos registrados aún.</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Motivo (nota) dialog — para Corregir Defecto y Cancelada */}
      <Dialog open={!!motivoDialog} onOpenChange={(v) => !v && setMotivoDialog(null)}>
        <DialogContent className="max-w-lg bg-[#FAF7F0] border-[#E5DFD3]">
          {motivoDialog && (() => {
            const cfg = ESTADOS_CON_MOTIVO[motivoDialog.estado] || {};
            const Icon = cfg.icon || AlertTriangle;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl text-[#1A0810] flex items-center gap-2">
                    <Icon className="h-5 w-5" strokeWidth={2} style={{ color: cfg.accent }} />
                    {cfg.title} · Escritura N° {motivoDialog.escritura?.numero}
                  </DialogTitle>
                  <DialogDescription className="text-[#6B5D57]">{cfg.label}</DialogDescription>
                </DialogHeader>
                <Textarea
                  data-testid="motivo-nota"
                  autoFocus
                  value={motivoDialog.nota || ""}
                  onChange={(e) => setMotivoDialog({ ...motivoDialog, nota: e.target.value })}
                  className="min-h-[110px] bg-white border-[#E5DFD3] text-[#1A0810]"
                  style={{ outlineColor: cfg.accent }}
                  placeholder={cfg.placeholder}
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMotivoDialog(null)} className="h-10 border-[#E5DFD3] text-[#3d2a2a]">Cancelar</Button>
                  <Button
                    data-testid="btn-confirm-motivo"
                    onClick={confirmarMotivo}
                    className="h-10 text-white"
                    style={{ backgroundColor: cfg.accent }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = cfg.accentHover)}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = cfg.accent)}
                  >
                    <Icon className="h-4 w-4 mr-2" strokeWidth={2} /> {cfg.confirm}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Export CSV dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-lg bg-[#FAF7F0] border-[#E5DFD3]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A0810]">Exportar registro</DialogTitle>
            <DialogDescription className="text-[#6B5D57]">
              Descarga el registro de escrituras en formato CSV. Puedes filtrar por rango de fechas y estado (opcional).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Desde</Label>
                <Input
                  data-testid="export-desde"
                  type="date"
                  value={exportRange.desde}
                  onChange={(e) => setExportRange({ ...exportRange, desde: e.target.value })}
                  className={`mt-2 ${inputCls}`}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Hasta</Label>
                <Input
                  data-testid="export-hasta"
                  type="date"
                  value={exportRange.hasta}
                  onChange={(e) => setExportRange({ ...exportRange, hasta: e.target.value })}
                  className={`mt-2 ${inputCls}`}
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Estado (opcional)</Label>
              <Select value={exportRange.estado} onValueChange={(v) => setExportRange({ ...exportRange, estado: v })}>
                <SelectTrigger data-testid="export-estado" className={`mt-2 ${inputCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los estados</SelectItem>
                  {config.estados?.map((s) => (<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  const y = new Date().getFullYear();
                  setExportRange({ desde: `${y}-01-01`, hasta: `${y}-12-31`, estado: exportRange.estado });
                }}
                className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full bg-[#F5EEE0] border border-[#E5DFD3] text-[#3d2a2a] hover:border-[#7A1E2E] hover:text-[#7A1E2E]"
              >
                Año actual
              </button>
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
                  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
                  setExportRange({ desde: first, hasta: last, estado: exportRange.estado });
                }}
                className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full bg-[#F5EEE0] border border-[#E5DFD3] text-[#3d2a2a] hover:border-[#7A1E2E] hover:text-[#7A1E2E]"
              >
                Mes actual
              </button>
              <button
                type="button"
                onClick={() => setExportRange({ desde: "", hasta: "", estado: "todos" })}
                className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full bg-white border border-[#E5DFD3] text-[#6B5D57] hover:border-[#7A1E2E] hover:text-[#7A1E2E]"
              >
                Limpiar
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)} className="h-10 border-[#E5DFD3] text-[#3d2a2a]">Cancelar</Button>
            <Button
              data-testid="btn-export-download"
              onClick={exportCsv}
              className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]"
            >
              <Download className="h-4 w-4 mr-2" strokeWidth={2} /> Descargar CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
